import { AxiosResponse } from 'axios';
import environment from '@environment';
import { CacheAreaKey } from 'core/config/cache-config';
import { EndpointHelper } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';
import { StorageHelper } from 'core/services/storage';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { ContentDTO } from '../umbraco/content';

export class PremiumRewardsService {
  private static apiBuilder = new EndpointHelper(`${environment.MAH_API_BASE_URL}/${environment.MAH_API_PREMIUM_STATUS}/${environment.MAH_API_VERSION}`);
  private static contentApiBuilder = new EndpointHelper(`${environment.UMBRACO_BASE_URI}`);

  static getPremiumStatus(customerNumber: string, country: string, accountNumber: string) {
    const url = this.apiBuilder.createUrl('SubscriberPremiumStatus')
      .addRouteParam('GetSubscriberStatus')
      .addQueryParam('customerNumber', customerNumber)
      .build();

    const reqBody = {
      businessUnit: SiteConfigStore.businessUnit(),
      channel: SiteConfigStore.channel(),
      country: country,
      customerNumber: customerNumber,
      accountNumber: accountNumber
    }

    return HttpClient.GetInstance.post<any, any>(url, reqBody)
      .then(
        res => res?.data?.subscriberStatus?.length ? res?.data?.subscriberStatus[0] : null,
        error => {
          if (error.status === 404) { // NOTE: this is arbitrary API stuff (404 means the user doesn't have premium)
            return null;
          }
          return Promise.reject(error);
        });
  }

  static getPremiumContent(): Promise<any[]> {
    const storedItems = (StorageHelper<any[]>).GetInstance.getFromCache('content', `PremiumRewards`, CacheAreaKey.Content);

    if (storedItems) {
      return Promise.resolve(storedItems as any[]);
    }

    const url = this.contentApiBuilder
      .createUrl('rewards/premium')
      .addBaseRouteParam('global-site-configuration')
      .addRouteParam('ujson')
      .addQueryParam('children', 'RewardPromos')
      .addQueryParam(
        'select',
        'RewardId,Icon,Type,Title,ActionLink,Logo,BackgroundImage,CompletedImage,PromotionCode,PrinciplePackage,StartDate,EndDate,LabelIcon,TagLabelLead,TagLabel,Descriptor,DescriptorMobile,TagLine,BodyText,ErrorMessage,AcceptedMessage,ClaimedMessage,TermsAndConditions,AcceptedButton,RewardLevel'
      )
      .addQueryParam(
        'resolveMedia',
        'Logo,BackgroundImage,CompletedImage,PrinciplePackage'
      )
      .addQueryParam('noCache', true)
      .build();

    return HttpClient.GetInstance.get<any>(url)
      .then((res: AxiosResponse<any>) => res?.data?.entities)
      .then((entities: ContentDTO[]) => (StorageHelper<any[]>).GetInstance.setCacheAndReturn(
        'content',
        CacheAreaKey.Content,
        entities.map((entity) => entity.properties as any),
        `PremiumRewards`
      ) as any[]);
  }

  static getPremiumLandingContent(): Promise<any> {
    const storedItems = (StorageHelper<any[]>).GetInstance.getFromCache('content', `PremiumRewardLanding`, CacheAreaKey.Content);

    if (storedItems) {
      return Promise.resolve(storedItems as any[]);
    }

    const url = this.contentApiBuilder
      .createUrl('rewards/premium-landing')
      .addBaseRouteParam('global-site-configuration')
      .addRouteParam('ujson')
      .addQueryParam('children', 'ImageDescriptionComponent')
      .addQueryParam(
        'select',
        'Header,SectionTitle,Image,ImageRight,Body,ButtonText,PrimaryButton,SecondaryButton'
      )
      .addQueryParam(
        'resolveMedia',
        'Image'
      )
      .addQueryParam('noCache', true)
      .build();

    return HttpClient.GetInstance.get<any>(url).then((res: AxiosResponse<any>) => (StorageHelper<any[]>).GetInstance.setCacheAndReturn(
      'content',
      CacheAreaKey.Content,
      res?.data?.properties,
      `PremiumRewardLanding`
    ) as any[]);
  }
}